import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  runTransaction,
} from 'firebase/firestore';
import { ref, uploadString, getDownloadURL, deleteObject } from 'firebase/storage';

import { firestore, auth, storage } from './firebase/config';
import { env } from '@/config/env';
import { USE_API, api } from '@/services/api/client';

export interface TestStep {
  id: string;
  grupo: string;
  titulo: string;
  descripcion: string;
  run: () => Promise<{ ok: true; detalle?: string } | { ok: false; error: string }>;
}

const TEST_PREFIX = '__diagnostico__';

function nowId() {
  return `${TEST_PREFIX}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function buildTests(): TestStep[] {
  if (USE_API) {
    return [
      {
        id: 'api-health',
        grupo: 'Conexión',
        titulo: 'API alcanzable',
        descripcion: 'GET /api/health responde.',
        run: async () => {
          const r = await api.get('/health');
          return r?.ok ? { ok: true, detalle: 'API OK' } : { ok: false, error: 'Sin respuesta.' };
        },
      },
      {
        id: 'api-me',
        grupo: 'Conexión',
        titulo: 'Sesión válida',
        descripcion: 'GET /api/auth/me devuelve el perfil.',
        run: async () => {
          const r = await api.get('/auth/me');
          return r?.perfil ? { ok: true, detalle: r.perfil.nombre } : { ok: false, error: 'Sin perfil.' };
        },
      },
      {
        id: 'api-productos',
        grupo: 'Lecturas',
        titulo: 'Listar farmacia',
        descripcion: 'GET /api/productos_venta.',
        run: async () => {
          const r = await api.get('/productos_venta');
          return { ok: true, detalle: `${r?.length ?? 0} productos` };
        },
      },
      {
        id: 'api-movimientos',
        grupo: 'Lecturas',
        titulo: 'Listar gastos',
        descripcion: 'GET /api/movimientos.',
        run: async () => {
          const r = await api.get('/movimientos');
          return { ok: true, detalle: `${r?.length ?? 0} movimientos` };
        },
      },
    ];
  }
  return [
    // ============= CONEXIÓN =============
    {
      id: 'auth-current-user',
      grupo: 'Conexión',
      titulo: 'Usuario autenticado',
      descripcion: 'Verifica que hay sesión activa de Firebase Auth.',
      run: async () => {
        if (!auth.currentUser) return { ok: false, error: 'No hay sesión activa.' };
        return { ok: true, detalle: `UID ${auth.currentUser.uid.slice(0, 12)}…` };
      },
    },
    {
      id: 'firestore-ping',
      grupo: 'Conexión',
      titulo: 'Firestore alcanzable',
      descripcion: 'Lee la colección users con limit(1).',
      run: async () => {
        const snap = await getDocs(query(collection(firestore, 'users'), limit(1)));
        return { ok: true, detalle: `${snap.size} doc leído` };
      },
    },
    {
      id: 'storage-ping',
      grupo: 'Conexión',
      titulo: 'Storage alcanzable',
      descripcion: 'Sube y borra un archivo de test.',
      run: async () => {
        const path = `_diagnostico/${nowId()}.txt`;
        const r = ref(storage, path);
        await uploadString(r, 'ping');
        await getDownloadURL(r);
        await deleteObject(r);
        return { ok: true };
      },
    },

    // ============= LECTURAS =============
    {
      id: 'read-productos-venta',
      grupo: 'Lecturas',
      titulo: 'Listar productos a la venta',
      descripcion: 'Lee productos_venta ordenado por nombre.',
      run: async () => {
        const snap = await getDocs(query(collection(firestore, 'productos_venta'), orderBy('nombre')));
        return { ok: true, detalle: `${snap.size} productos` };
      },
    },
    {
      id: 'read-insumos',
      grupo: 'Lecturas',
      titulo: 'Listar insumos internos',
      descripcion: 'Lee insumos_internos ordenado por nombre.',
      run: async () => {
        const snap = await getDocs(query(collection(firestore, 'insumos_internos'), orderBy('nombre')));
        return { ok: true, detalle: `${snap.size} insumos` };
      },
    },
    {
      id: 'read-proveedores',
      grupo: 'Lecturas',
      titulo: 'Listar proveedores',
      descripcion: 'Lee colección de proveedores.',
      run: async () => {
        const snap = await getDocs(collection(firestore, 'proveedores'));
        return { ok: true, detalle: `${snap.size} proveedores` };
      },
    },
    {
      id: 'read-movimientos-clinica',
      grupo: 'Lecturas',
      titulo: 'Movimientos del bloque clínica',
      descripcion: 'Query con where + orderBy (requiere índice compuesto).',
      run: async () => {
        const snap = await getDocs(
          query(
            collection(firestore, 'movimientos'),
            where('bloque', '==', 'clinica'),
            orderBy('fecha', 'desc'),
            limit(20),
          ),
        );
        return { ok: true, detalle: `${snap.size} movimientos` };
      },
    },
    {
      id: 'read-stock-bajo',
      grupo: 'Lecturas',
      titulo: 'Productos con stock bajo',
      descripcion: 'Where stockBajo == true (requiere índice).',
      run: async () => {
        const snap = await getDocs(
          query(collection(firestore, 'productos_venta'), where('stockBajo', '==', true)),
        );
        return { ok: true, detalle: `${snap.size} con stock bajo` };
      },
    },

    // ============= ESCRITURAS =============
    {
      id: 'write-proveedor',
      grupo: 'Escrituras',
      titulo: 'Crear/actualizar/borrar proveedor',
      descripcion: 'Ciclo CRUD completo en proveedores.',
      run: async () => {
        const id = nowId();
        const r = doc(firestore, 'proveedores', id);
        await setDoc(r, {
          nombre: '__test__',
          whatsapp: '+59100000000',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        await updateDoc(r, { nombre: '__test__updated', updatedAt: serverTimestamp() });
        const snap = await getDoc(r);
        if (!snap.exists()) return { ok: false, error: 'No se encontró tras crear.' };
        if (snap.data()?.nombre !== '__test__updated') {
          return { ok: false, error: 'El update no aplicó.' };
        }
        await deleteDoc(r);
        return { ok: true, detalle: 'Crear → Update → Delete OK' };
      },
    },
    {
      id: 'write-producto-stockBajo',
      grupo: 'Escrituras',
      titulo: 'Stock bajo se autocalcula',
      descripcion: 'Crea un producto con stock < stockMin y verifica que stockBajo = true.',
      run: async () => {
        const id = nowId();
        const r = doc(firestore, 'productos_venta', id);
        const stock = 1;
        const stockMin = 5;
        await setDoc(r, {
          nombre: '__test__',
          presentacion: 'Test',
          stock,
          stockMin,
          stockBajo: stock <= stockMin,
          precioCompra: 0,
          precioVenta: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        const snap = await getDoc(r);
        const ok = snap.data()?.stockBajo === true;
        await deleteDoc(r);
        return ok ? { ok: true, detalle: 'stockBajo correcto' } : { ok: false, error: 'stockBajo mal calculado.' };
      },
    },
    {
      id: 'write-movimiento',
      grupo: 'Escrituras',
      titulo: 'Crear y borrar movimiento contable',
      descripcion: 'Movimiento de prueba en el bloque clínica.',
      run: async () => {
        const id = nowId();
        const r = doc(firestore, 'movimientos', id);
        await setDoc(r, {
          bloque: 'clinica',
          tipo: 'ingreso',
          categoria: 'Consulta',
          monto: 100,
          metodoPago: 'efectivo',
          descripcion: '__test__ diagnóstico',
          fecha: Timestamp.now(),
          autorUid: auth.currentUser?.uid ?? '',
          autorNombre: '__test__',
          createdAt: serverTimestamp(),
        });
        await deleteDoc(r);
        return { ok: true };
      },
    },

    // ============= TRANSACCIONES =============
    {
      id: 'transaction-stock',
      grupo: 'Transacciones',
      titulo: 'Transacción atómica de stock',
      descripcion: 'Crea producto con stock=10, lo descuenta a 7 en transacción y verifica.',
      run: async () => {
        const id = nowId();
        const r = doc(firestore, 'insumos_internos', id);
        await setDoc(r, {
          nombre: '__test__',
          presentacion: 'Test',
          stock: 10,
          stockMin: 2,
          stockBajo: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        try {
          await runTransaction(firestore, async (tx) => {
            const snap = await tx.get(r);
            if (!snap.exists()) throw new Error('producto no existe');
            const stock = (snap.data().stock as number) ?? 0;
            tx.update(r, { stock: stock - 3, updatedAt: serverTimestamp() });
          });
          const final = await getDoc(r);
          const ok = final.data()?.stock === 7;
          await deleteDoc(r);
          return ok ? { ok: true, detalle: 'Stock 10 → 7 atómico' } : { ok: false, error: 'Stock final inesperado.' };
        } catch (e: any) {
          await deleteDoc(r).catch(() => {});
          return { ok: false, error: e?.message ?? 'Transacción falló.' };
        }
      },
    },

    // ============= STORAGE =============
    {
      id: 'storage-upload-caso',
      grupo: 'Storage',
      titulo: 'Subir archivo en carpeta de doctor',
      descripcion: 'Sube un texto a casos/{uid}/test/ y lo elimina.',
      run: async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return { ok: false, error: 'Sin sesión.' };
        const path = `casos/${uid}/_diagnostico/${nowId()}.txt`;
        const r = ref(storage, path);
        await uploadString(r, 'contenido de prueba');
        const url = await getDownloadURL(r);
        await deleteObject(r);
        return { ok: true, detalle: url.includes('http') ? 'URL pública generada' : 'OK' };
      },
    },

    // ============= REGLAS DE SEGURIDAD =============
    {
      id: 'rules-self-update',
      grupo: 'Reglas',
      titulo: 'Puedo actualizar mi propio nombre',
      descripcion: 'Permitido por las reglas de Firestore.',
      run: async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return { ok: false, error: 'Sin sesión.' };
        const r = doc(firestore, 'users', uid);
        const snap = await getDoc(r);
        if (!snap.exists()) return { ok: false, error: 'Mi perfil no existe.' };
        const original = snap.data().nombre;
        await updateDoc(r, { nombre: original });
        return { ok: true, detalle: 'Update permitido' };
      },
    },
    {
      id: 'rules-deny-other-user',
      grupo: 'Reglas',
      titulo: 'No puedo modificar otro usuario (si no soy admin)',
      descripcion: 'Este test pasa si soy admin (puede) o falla controladamente si no soy.',
      run: async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return { ok: false, error: 'Sin sesión.' };
        const otherSnap = await getDocs(
          query(collection(firestore, 'users'), where('rol', '!=', 'admin'), limit(1)),
        );
        const other = otherSnap.docs.find((d) => d.id !== uid);
        if (!other) return { ok: true, detalle: 'No hay otro usuario para probar.' };
        try {
          // Escritura no destructiva: reescribimos el MISMO nombre (no altera datos).
          const nombreActual = other.data().nombre;
          await updateDoc(doc(firestore, 'users', other.id), { nombre: nombreActual });
          return { ok: true, detalle: 'Permitido (sos admin)' };
        } catch (e: any) {
          if (e?.code === 'permission-denied') return { ok: true, detalle: 'Bloqueado correctamente' };
          return { ok: false, error: e?.message ?? 'Error inesperado.' };
        }
      },
    },
  ];
}

export interface TestRun {
  id: string;
  status: 'idle' | 'running' | 'pass' | 'fail';
  ms?: number;
  error?: string;
  detalle?: string;
}

export async function runTest(t: TestStep): Promise<TestRun> {
  const start = Date.now();
  try {
    const res = await t.run();
    const ms = Date.now() - start;
    if (res.ok) return { id: t.id, status: 'pass', ms, detalle: res.detalle };
    return { id: t.id, status: 'fail', ms, error: res.error };
  } catch (e: any) {
    return {
      id: t.id,
      status: 'fail',
      ms: Date.now() - start,
      error: e?.message ?? String(e),
    };
  }
}

export const ENV_INFO = () => ({
  ambiente: env,
  proyecto: 'clinicat-f314b',
});
